import React from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useSelector } from 'react-redux'
import Slider from 'react-slick'

import {
    PrevArrow,
    NextArrow,
} from 'components/common/ImageCarouselView/components'
import { Modal } from 'components/Modal'
import { Color } from 'constants/color'
import * as SocketActions from 'constants/socket'
import {
    noteEventEmitter,
    noteKeys,
    emitOpenFullScreenNoteModal,
} from 'helpers/events'
import {
    generateRandomId,
    generateDummyNotes,
    isDemo,
} from 'helpers/miscellaneous'
import { getTime } from 'helpers/time'
import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import {
    dispatchPopulateDummyNotesMessages,
    dispatchSendSocketMessage,
} from 'store/dispatcher'
import { _userId } from 'store/selectors/User'
import { _websocketNoteMsg, _websocketUserId } from 'store/selectors/WebSocket'
import { keyToUse } from 'utils/hotKey'

import {
    Title,
    ButtonContainer,
    Button,
    ItemContainer,
    ItemImageContainer,
    Item,
    SliderContainer,
    Details,
    DetailInfo,
    MaximizeIconContainer,
    MaximizeIcon,
    FallbackContainer,
} from './styles'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import './styles.css'

const commonStyles = {
    modal: {
        backgroundColor: Color.unknownColor19,
    },
    closeButton: {
        filter: 'brightness(0) invert(1)',
    },
}

const styles = {
    modal: {
        ...commonStyles.modal,
        maxWidth: 784,
        maxHeight: 420,
    },
    closeButton: {
        ...commonStyles.closeButton,
    },
}

const paginationModeStyles = {
    modal: {
        ...commonStyles.modal,
        maxWidth: 860,
        maxHeight: 432,
    },
    closeButton: {
        ...commonStyles.closeButton,
    },
}

const NextArrowComponent = <NextArrow />
const PrevArrowComponent = <PrevArrow />

let isDummyNotesPopulated = false

const Notes = () => {
    const notes = useSelector(_websocketNoteMsg)
    const userId = useSelector(_websocketUserId)

    const [activeUser, setActiveUser] = React.useState({})
    const [isModalOpen, setIsModalOpen] = React.useState(false)
    const [showAllUploads, setShowAllUploads] = React.useState(false)

    const localUserId = useSelector(_userId)

    const sliderContainerRef = React.useRef(null)
    const sliderRef = React.useRef(null)
    const {
        room: {
            localParticipant: { identity: localUserIdentity },
        },
    } = useVideoContext()

    React.useEffect(() => {
        const openModal = ({ userId, userName }) => {
            setIsModalOpen(true)
            setActiveUser({ userId, userName })
        }

        noteEventEmitter.addListener(noteKeys.OPEN_NOTE_MODAL, openModal)

        return () => {
            noteEventEmitter.removeListener(noteKeys.OPEN_NOTE_MODAL, openModal)
        }
    }, [])

    useHotkeys(
        `${keyToUse}+p`,
        e => {
            e.preventDefault()
            if (!isDummyNotesPopulated) {
                const [uuid, fullName] = localUserIdentity.split(':')
                const dummyNotes = generateDummyNotes({ uuid, fullName })
                if (isDemo) {
                    const data = {
                        action: SocketActions.ELSEWHERE,
                        key: dummyNotes,
                        user_id: localUserId,
                    }
                    dispatchSendSocketMessage(data)
                } else {
                    dispatchPopulateDummyNotesMessages(dummyNotes)
                }
                isDummyNotesPopulated = true
            }
        },
        [localUserIdentity]
    )

    const userNotes = React.useMemo(() => {
        const onExpandToggle = (index, filteredNotes) => {
            emitOpenFullScreenNoteModal(filteredNotes, index)
        }

        const generateRandomValueForDummyItem = () => {
            return { note: { id: generateRandomId(), isDummy: true } }
        }

        const filteredNotes = isDemo
            ? showAllUploads
                ? notes
                : notes.filter(
                      item =>
                          item.note.user_id ===
                          (userId ? userId : activeUser.userId)
                  )
            : showAllUploads
            ? notes
            : activeUser.userId
            ? notes.filter(item => item.note.user_id === activeUser.userId)
            : []

        const _filteredNotes =
            filteredNotes.length < 3
                ? [
                      ...filteredNotes,
                      ...Array.apply(null, {
                          length: 3 - filteredNotes.length,
                      }).map(Function.call, generateRandomValueForDummyItem),
                  ]
                : filteredNotes

        return _filteredNotes.map((item, index) => (
            <ItemContainer key={item.note.id}>
                <ItemImageContainer>
                    {!item.note.isDeleted && !item.note.isDummy ? (
                        <Item src={item.note.file} className="note-image" />
                    ) : (
                        <FallbackContainer>
                            {item.note.isDeleted
                                ? 'This image has been deleted'
                                : 'no image'}
                        </FallbackContainer>
                    )}

                    {!item.note.isDeleted && !item.note.isDummy ? (
                        <MaximizeIconContainer
                            onClick={() => onExpandToggle(index, filteredNotes)}
                            className="expand-icon-container"
                        >
                            <MaximizeIcon src="/img/artefacts/whiteboard/toolbar/maximize.svg" />
                        </MaximizeIconContainer>
                    ) : null}
                </ItemImageContainer>

                {!item.note.isDeleted &&
                !item.note.isDummy &&
                showAllUploads ? (
                    <Details isPaginationRequired={filteredNotes.length > 3}>
                        <DetailInfo>{item.note.name} </DetailInfo>
                        <DetailInfo>{getTime(item.note.created)}</DetailInfo>
                    </Details>
                ) : null}
            </ItemContainer>
        ))
    }, [notes, activeUser, showAllUploads, sliderRef])

    const onCloseModal = () => {
        // then close modal
        setIsModalOpen(false)
        // reset show all state
        setShowAllUploads(false)
    }

    const onTriggerClick = () => {
        setShowAllUploads(_showAllUploads => !_showAllUploads)
    }

    const isPaginationRequired = userNotes.length > 3

    const _styles = isPaginationRequired ? paginationModeStyles : styles

    return (
        <Modal
            isModalOpen={isModalOpen}
            onCloseModal={onCloseModal}
            styles={_styles}
        >
            <Title>Uploads</Title>

            <SliderContainer
                ref={sliderContainerRef}
                isPaginationRequired={isPaginationRequired}
            >
                <Slider
                    ref={sliderRef}
                    dots={false}
                    infinite={false}
                    speed={500}
                    slidesToShow={3}
                    slidesToScroll={1}
                    nextArrow={NextArrowComponent}
                    prevArrow={PrevArrowComponent}
                >
                    {userNotes}
                </Slider>
            </SliderContainer>

            <ButtonContainer>
                <Button onClick={onTriggerClick}>
                    {showAllUploads
                        ? `view uploads from ${activeUser.userName}`
                        : 'view uploads from all'}
                </Button>
            </ButtonContainer>
        </Modal>
    )
}

export { Notes }
